<script>
import { mapActions } from 'vuex';
import Vue from 'vue';
import { postRequest } from '../../helpers/request';
import { datadogLogs } from '@datadog/browser-logs';

const CHARGEBEE_SCRIPT_URL = 'https://js.chargebee.com/v2/chargebee.js';
const CHARGEBEE_SITE = window.DJANGO_REQUEST.app.isProduction
  ? 'farmlinksolutions'
  : 'farmlinksolutions-test';

export default {
  name: 'Chargebee',

  data() {
    return {
      instance: null,
    };
  },

  beforeCreate() {
    Vue.prototype.$chargebee = {
      isReady: false,
      openSubscriptionCheckout: () => {},
      openBundleCheckout: () => {},
      closeAll: () => {},
    };
  },

  async created() {
    await Promise.all([this.initializeChargebee(), this.getAllItemPrices?.()]);
  },

  beforeDestroy() {
    this.$unloadScript(CHARGEBEE_SCRIPT_URL);
    delete Vue.prototype.$chargebee;
  },

  methods: {
    ...mapActions?.('chargebee', ['getAllItemPrices']) ?? {},

    async initializeChargebee() {
      try {
        await this.$loadScript(CHARGEBEE_SCRIPT_URL);
        this.instance = Chargebee.init({ site: CHARGEBEE_SITE, iframeOnly: true });
        Vue.prototype.$chargebee = {
          isReady: true,
          openSubscriptionCheckout: this.openSubscriptionCheckout,
          openBundleCheckout: this.openBundleCheckout,
          closeAll: this.closeAll,
        };
      } catch (e) {
        datadogLogs.logger.error('Chargebee init error', {}, e);
      }
    },

    ensureReady() {
      const ready = !!(this.instance && this.$chargebee && this.$chargebee.isReady);
      if (!ready) datadogLogs.logger.warn('Chargebee not ready');
      return ready;
    },

    openSubscriptionCheckout(options = {}) {
      if (!this.ensureReady()) return;

      const { subscriptionCart = {}, coupon = null, callbacks = {} } = options;

      const payload = {
        subscriptionCart: {
          selectedPlan: subscriptionCart?.selectedPlan?.id
            ? { id: subscriptionCart.selectedPlan.id }
            : null,
          selectedAddons: Array.isArray(subscriptionCart?.selectedAddons)
            ? subscriptionCart.selectedAddons.map((addon) => (
              { id: addon.id,
                quantity: addon.quantity || 1,
                is_default: addon.is_default || false }))
            : [],
          selectedSinoa: subscriptionCart?.selectedSinoa
            ? { id: subscriptionCart.selectedSinoa, quantity: 1 }
            : null,
        },
        coupon,
      };

      try {
        this.instance.openCheckout({
          hostedPage: () => postRequest('/chatbot/checkout/existing/', payload),
          ...callbacks,
        });
      } catch (e) {
        datadogLogs.logger.error('Chargebee open subscription checkout error', {}, e);
      }
    },

    openBundleCheckout(options = {}) {
      if (!this.ensureReady()) return;

      const { bundleCart = {}, coupon = null, callbacks = {} } = options;

      const payload = {
        bundle: bundleCart?.id
          ? { id: bundleCart.id, quantity: bundleCart.quantity || 1 }
          : null,
        coupon,
      };

      try {
        this.instance.openCheckout({
          hostedPage: () => postRequest('/chatbot/checkout/one_time/', payload),
          ...callbacks,
        });
      } catch (e) {
        datadogLogs.logger.error('Chargebee open bundle checkout error', {}, e);
      }
    },

    closeAll() {
      if (this.instance && typeof this.instance.closeAll === 'function') {
        this.instance.closeAll();
      }
    },
  },

  render() {
    return null;
  },
};
</script>
